import cx from 'classnames';
import { FunctionComponent, ReactNode } from 'react';
import Modal, { Props as ModalProps } from 'react-modal';

import Button from '../button';
import IconClose from '../icons/close';

import classes from './modal.module.css';

type TBPModal = {
  variant?: 'fullscreen' | '1/2' | '1/3' | 'alert' | 'container' | 'auto';
  title?: ReactNode;
  titleVariant?: 'left' | 'center';
  showClose?: boolean;
  isOverflowVisible?: boolean;
  zIndex?: number;
} & ModalProps;

const sizeRelation: { [key: string]: number } = {
  fullscreen: 100,
  '1/2': 50,
  '1/3': 33,
};

const widthRelation: { [key: string]: number } = {
  fullscreen: 100,
  '1/2': 50,
  '1/3': 33,
};

const BPModal: FunctionComponent<TBPModal> = ({
  variant = '1/3',
  title,
  titleVariant = 'center',
  onRequestClose,
  showClose = true,
  className,
  isOverflowVisible,
  zIndex = 99,
  ...props
}) => (
  <Modal
    ariaHideApp={false}
    preventScroll
    shouldCloseOnEsc
    closeTimeoutMS={300}
    style={{
      overlay: {
        background: 'rgba(0, 0, 0, 0.25)',
        zIndex: zIndex,
      },
      content: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform:
          variant !== 'fullscreen' ? 'translate(-50%, -50%)' : undefined,
        backgroundColor: 'white',
        border: 'none',
        borderRadius: 0,
        overflow: isOverflowVisible ? '' : 'auto',
        height: variant === 'fullscreen' ? undefined : 'auto',
        width: (() => {
          switch (variant) {
            case 'fullscreen':
              return '100vw';
            case '1/2':
              return '50vw';
            case '1/3':
              return '33vw';
            case 'auto':
              return 'auto';
            case 'alert':
            default:
              return undefined;
          }
        })(),
        maxHeight: `${sizeRelation[variant] === 100 ? '100vh' : '90vh'}`,
        outline: 'none',
      },
    }}
    portalClassName={cx(
      variant === 'fullscreen' || variant === 'alert'
        ? classes.modalFull
        : undefined,
      classes.modal
    )}
    className={cx(
      'z-40',
      variant === 'alert' && 'w-11/12 lg:max-w-screen-lg',
      variant === 'fullscreen' ? 'w-full p-0 lg:p-12' : 'p-4 lg:p-8 lg:pt-8',
      { container: variant === 'container' }
    )}
    onAfterOpen={() => (document.body.style.overflow = 'hidden')}
    onAfterClose={() => (document.body.style.overflow = 'auto')}
    onRequestClose={onRequestClose}
    {...props}
  >
    {titleVariant === 'center' ? (
      <div
        className={cx(
          classes.titleCenterGrid,
          'py-3',
          'px-3',
          'lg:px-0',
          'grid',
          'align-top',
          'relative'
        )}
      >
        {title && (
          <h3
            className={cx(
              'font-bold',
              'col-start-2',
              'text-center',
              'mt-10',
              'lg:mt-0'
            )}
          >
            {title}
          </h3>
        )}
        {showClose && (
          <Button
            isFullWidth={false}
            className="absolute top-0 right-0 h-12 w-12 text-center"
            onClick={onRequestClose}
            data-cy="close-modal"
          >
            <IconClose className="h-4 w-4 inline-block text-neutral-80" />
          </Button>
        )}
      </div>
    ) : (
      <div className="py-3 px-3 lg:px-0 flex align-top justify-between relative">
        {title && <h3 className={cx('font-bold mr-12')}>{title}</h3>}
        {showClose && (
          <Button
            disableHeight={true}
            isFullWidth={false}
            className={cx(
              'absolute top-0 bottom-0 px-0 right-0 w-12 text-center'
            )}
            onClick={onRequestClose}
            data-cy="close-modal"
          >
            <IconClose className="h-4 w-4 inline-block text-neutral-80" />
          </Button>
        )}
      </div>
    )}
    {props.children}
  </Modal>
);

export default BPModal;
